import Cookies from 'js-cookie'
import type { CookieAttributes } from 'js-cookie'

const TOKEN_KEY = 'admin_token'
const TOKEN_TIMESTAMP_KEY = 'admin_token_timestamp'

// Check if app is running in an iframe
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// Check if cookies are working (test by setting and reading)
const areCookiesEnabled = (): boolean => {
  try {
    const testKey = '__test_cookie__'
    Cookies.set(testKey, 'test', { sameSite: 'none', secure: true })
    const result = Cookies.get(testKey) === 'test'
    Cookies.remove(testKey)
    return result
  } catch (e) {
    return false
  }
}

const COOKIE_OPTIONS: CookieAttributes = {
  expires: 10,
  secure: true,
  sameSite: isInIframe() ? 'none' : 'strict',
}

// In iframe with blocked 3rd party cookies, use sessionStorage (more secure than localStorage)
// sessionStorage is cleared when the tab is closed
const useSessionStorage = isInIframe() && !areCookiesEnabled()

export const setToken = (token: string): void => {
  if (useSessionStorage) {
    // Use sessionStorage in iframe when cookies are blocked
    // Also store timestamp for additional security validation
    try {
      sessionStorage.setItem(TOKEN_KEY, token)
      sessionStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString())
    } catch (e) {
      console.error('Failed to store token in sessionStorage:', e)
    }
  } else {
    // Use cookies when available
    Cookies.set(TOKEN_KEY, token, COOKIE_OPTIONS)
  }
}

export const getToken = (): string | undefined => {
  if (useSessionStorage) {
    try {
      const token = sessionStorage.getItem(TOKEN_KEY)
      const timestamp = sessionStorage.getItem(TOKEN_TIMESTAMP_KEY)

      // Validate token age (10 days max, same as cookie expiration)
      if (token && timestamp) {
        const tokenAge = Date.now() - parseInt(timestamp, 10)
        const maxAge = 10 * 24 * 60 * 60 * 1000 // 10 days in milliseconds

        if (tokenAge > maxAge) {
          // Token expired, remove it
          removeToken()
          return undefined
        }
        return token
      }
      return token || undefined
    } catch (e) {
      console.error('Failed to get token from sessionStorage:', e)
      return undefined
    }
  } else {
    return Cookies.get(TOKEN_KEY)
  }
}

export const removeToken = (): void => {
  if (useSessionStorage) {
    try {
      sessionStorage.removeItem(TOKEN_KEY)
      sessionStorage.removeItem(TOKEN_TIMESTAMP_KEY)
    } catch (e) {
      console.error('Failed to remove token from sessionStorage:', e)
    }
  } else {
    Cookies.remove(TOKEN_KEY, COOKIE_OPTIONS)
  }
}

export const removeDeprecatedToken = (): void => {
  // TODO remove in future versions
  Cookies.remove('token')
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')
}
